import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
  SmsTriggerState, 
  SmsFilterOptions, 
  SmsTriggerFilters, 
  SmsTriggerPreview, 
  SmsTriggerSendResponse, 
  SmsTriggerStatus,
  RenewalPolicy,
  SmsPolicyFilters,
  PoliciesResponse
} from '@/types/sms';

const initialState: SmsTriggerState = {
  // Filter options
  filterOptions: null,
  filterOptionsLoading: false,
  filterOptionsError: null,
  
  // Dependent filter options
  dependentSubChannels: [],
  dependentSubChannelsLoading: false,
  dependentSubChannelsError: null,
  
  // Dependent LOBs (filtered by sub-channel)
  dependentLOBs: [],
  dependentLOBsLoading: false,
  dependentLOBsError: null,
  
  // Dependent product names (filtered by LOB)
  dependentProductNames: [],
  dependentProductNamesLoading: false,
  dependentProductNamesError: null,
  
  // Dependent channels (filtered by product_name)
  dependentChannels: [],
  dependentChannelsLoading: false,
  dependentChannelsError: null,
  
  // Preview
  preview: null,
  previewLoading: false,
  previewError: null,
  
  // Trigger
  triggerResponse: null,
  triggerLoading: false,
  triggerError: null,
  
  // Status tracking
  status: null,
  statusLoading: false,
  statusError: null,
  
  // Current filters
  currentFilters: {},
  
  // UI state
  isPreviewVisible: false,
  isStatusVisible: false,
  isIndividualSelectionMode: false,  // Default to bulk mode
  
  // Individual policy selection
  policies: [],
  policiesLoading: false,
  policiesError: null,
  selectedPolicyNumbers: [],
  policyFilters: {
    page: 1,
    page_size: 10,
    sms_type: 'notice_only'  // Default SMS type
  },
  paginationInfo: null
};

const smsTriggerSlice = createSlice({
  name: 'smsTrigger',
  initialState,
  reducers: {
    // Filter Options Actions
    fetchSmsFilterOptionsRequested: (state) => {
      state.filterOptionsLoading = true;
      state.filterOptionsError = null;
    },
    fetchSmsFilterOptionsSucceeded: (state, action: PayloadAction<SmsFilterOptions>) => {
      state.filterOptionsLoading = false;
      state.filterOptions = action.payload;
    },
    fetchSmsFilterOptionsFailed: (state, action: PayloadAction<string>) => {
      state.filterOptionsLoading = false;
      state.filterOptionsError = action.payload;
    },
    
    // Dependent Filter Options Actions
    fetchSmsDependentSubChannelsRequested: (state, action: PayloadAction<string | string[]>) => {
      state.dependentSubChannelsLoading = true;
      state.dependentSubChannelsError = null;
    },
    fetchSmsDependentSubChannelsSucceeded: (state, action: PayloadAction<string[]>) => {
      state.dependentSubChannelsLoading = false;
      state.dependentSubChannels = action.payload;
    },
    fetchSmsDependentSubChannelsFailed: (state, action: PayloadAction<string>) => {
      state.dependentSubChannelsLoading = false;
      state.dependentSubChannelsError = action.payload;
    },
    clearSmsDependentSubChannels: (state) => {
      state.dependentSubChannels = [];
      state.dependentSubChannelsLoading = false;
      state.dependentSubChannelsError = null;
    },
    
    // Dependent LOBs Actions
    fetchSmsDependentLOBsRequested: (state, action: PayloadAction<string>) => {
      state.dependentLOBsLoading = true;
      state.dependentLOBsError = null;
    },
    fetchSmsDependentLOBsSucceeded: (state, action: PayloadAction<string[]>) => {
      state.dependentLOBsLoading = false;
      state.dependentLOBs = action.payload;
    },
    fetchSmsDependentLOBsFailed: (state, action: PayloadAction<string>) => {
      state.dependentLOBsLoading = false;
      state.dependentLOBsError = action.payload;
    },
    clearSmsDependentLOBs: (state) => {
      state.dependentLOBs = [];
      state.dependentLOBsLoading = false;
      state.dependentLOBsError = null;
    },
    
    // Dependent Product Names Actions
    fetchSmsDependentProductNamesRequested: (state, action: PayloadAction<string | string[]>) => {
      state.dependentProductNamesLoading = true;
      state.dependentProductNamesError = null;
    },
    fetchSmsDependentProductNamesSucceeded: (state, action: PayloadAction<string[]>) => {
      state.dependentProductNamesLoading = false;
      state.dependentProductNames = action.payload;
    },
    fetchSmsDependentProductNamesFailed: (state, action: PayloadAction<string>) => {
      state.dependentProductNamesLoading = false;
      state.dependentProductNamesError = action.payload;
    },
    clearSmsDependentProductNames: (state) => {
      state.dependentProductNames = [];
      state.dependentProductNamesLoading = false;
      state.dependentProductNamesError = null;
    },
    
    // Dependent Channels Actions
    fetchSmsDependentChannelsRequested: (state, action: PayloadAction<string | string[]>) => {
      state.dependentChannelsLoading = true;
      state.dependentChannelsError = null;
    },
    fetchSmsDependentChannelsSucceeded: (state, action: PayloadAction<string[]>) => {
      state.dependentChannelsLoading = false;
      state.dependentChannels = action.payload;
    },
    fetchSmsDependentChannelsFailed: (state, action: PayloadAction<string>) => {
      state.dependentChannelsLoading = false;
      state.dependentChannelsError = action.payload;
    },
    clearSmsDependentChannels: (state) => {
      state.dependentChannels = [];
      state.dependentChannelsLoading = false;
      state.dependentChannelsError = null;
    },
    
    // Preview Actions
    smsTriggerPreviewRequested: (state, action: PayloadAction<SmsTriggerFilters>) => {
      state.previewLoading = true;
      state.previewError = null;
      state.currentFilters = action.payload;
    },
    smsTriggerPreviewSucceeded: (state, action: PayloadAction<SmsTriggerPreview>) => {
      state.previewLoading = false;
      state.preview = action.payload;
      state.isPreviewVisible = true;
    },
    smsTriggerPreviewFailed: (state, action: PayloadAction<string>) => {
      state.previewLoading = false;
      state.previewError = action.payload;
    },
    
    // Trigger Actions
    smsTriggerSendRequested: (state, action: PayloadAction<SmsTriggerFilters>) => {
      state.triggerLoading = true;
      state.triggerError = null;
      state.currentFilters = action.payload;
    },
    smsTriggerSendSucceeded: (state, action: PayloadAction<SmsTriggerSendResponse>) => {
      state.triggerLoading = false;
      state.triggerResponse = action.payload;
      state.isStatusVisible = true;
    },
    smsTriggerSendFailed: (state, action: PayloadAction<string>) => {
      state.triggerLoading = false;
      state.triggerError = action.payload;
    },
    
    // Status Actions
    smsTriggerStatusRequested: (state, action: PayloadAction<string>) => {
      state.statusLoading = true;
      state.statusError = null;
    },
    smsTriggerStatusSucceeded: (state, action: PayloadAction<SmsTriggerStatus>) => {
      state.statusLoading = false;
      state.status = action.payload;
    },
    smsTriggerStatusFailed: (state, action: PayloadAction<string>) => {
      state.statusLoading = false;
      state.statusError = action.payload;
    },
    
    // UI State Actions
    setSmsCurrentFilters: (state, action: PayloadAction<SmsTriggerFilters>) => {
      state.currentFilters = action.payload;
    },
    clearSmsPreview: (state) => {
      state.preview = null;
      state.isPreviewVisible = false;
      state.previewError = null;
    },
    clearSmsStatus: (state) => {
      state.status = null;
      state.isStatusVisible = false;
      state.statusError = null;
      state.triggerResponse = null;
    },
    resetSmsTrigger: (state) => {
      Object.assign(state, initialState);
    },
    
    // Individual Policy Selection Actions
    fetchPoliciesForSelectionRequested: (state, action: PayloadAction<SmsPolicyFilters>) => {
      state.policiesLoading = true;
      state.policiesError = null;
      state.policyFilters = { ...state.policyFilters, ...action.payload };
    },
    fetchPoliciesForSelectionSucceeded: (state, action: PayloadAction<PoliciesResponse>) => {
      state.policiesLoading = false;
      state.policies = action.payload.policies;
      state.paginationInfo = {
        total: action.payload.total,
        sms_sendable_count: action.payload.sms_sendable_count,
        page: action.payload.page,
        page_size: action.payload.page_size,
        total_pages: action.payload.total_pages
      };
    },
    fetchPoliciesForSelectionFailed: (state, action: PayloadAction<string>) => {
      state.policiesLoading = false;
      state.policiesError = action.payload;
    },
    
    togglePolicySelection: (state, action: PayloadAction<string>) => {
      const policyNumber = action.payload;
      // Find the policy to check if it's CONVERTED
      const policy = state.policies.find(p => p.policy_number === policyNumber);
      // Don't allow selection of CONVERTED policies
      if (policy?.lead_status === 'CONVERTED') {
        return;
      }
      const index = state.selectedPolicyNumbers.indexOf(policyNumber);
      if (index > -1) {
        state.selectedPolicyNumbers.splice(index, 1);
      } else {
        state.selectedPolicyNumbers.push(policyNumber);
      }
    },
    
    selectAllPolicies: (state) => {
      // Only select policies that are not CONVERTED
      state.selectedPolicyNumbers = state.policies
        .filter(p => p.lead_status !== 'CONVERTED')
        .map(p => p.policy_number);
    },
    
    clearPolicySelection: (state) => {
      state.selectedPolicyNumbers = [];
    },
    
    clearPolicies: (state) => {
      state.policies = [];
      state.paginationInfo = null;
      state.selectedPolicyNumbers = [];
      state.policiesError = null;
      state.policyFilters = {
        page: 1,
        page_size: 10,
        sms_type: 'notice_only'
      };
    },
    
    triggerIndividualSmsRequested: (state, action: PayloadAction<string[]>) => {
      state.triggerLoading = true;
      state.triggerError = null;
    },
    
    setPolicyFilters: (state, action: PayloadAction<SmsPolicyFilters>) => {
      state.policyFilters = { ...state.policyFilters, ...action.payload };
    },
    
    // Toggle individual selection mode
    setIndividualSelectionMode: (state, action: PayloadAction<boolean>) => {
      state.isIndividualSelectionMode = action.payload;
      // Clear selection when switching modes
      if (!action.payload) {
        state.selectedPolicyNumbers = [];
      }
    }
  }
});

export const {
  // Filter Options
  fetchSmsFilterOptionsRequested,
  fetchSmsFilterOptionsSucceeded,
  fetchSmsFilterOptionsFailed,
  
  // Dependent Filter Options
  fetchSmsDependentSubChannelsRequested,
  fetchSmsDependentSubChannelsSucceeded,
  fetchSmsDependentSubChannelsFailed,
  clearSmsDependentSubChannels,
  
  // Dependent LOBs
  fetchSmsDependentLOBsRequested,
  fetchSmsDependentLOBsSucceeded,
  fetchSmsDependentLOBsFailed,
  clearSmsDependentLOBs,
  
  // Dependent Product Names
  fetchSmsDependentProductNamesRequested,
  fetchSmsDependentProductNamesSucceeded,
  fetchSmsDependentProductNamesFailed,
  clearSmsDependentProductNames,
  
  // Dependent Channels
  fetchSmsDependentChannelsRequested,
  fetchSmsDependentChannelsSucceeded,
  fetchSmsDependentChannelsFailed,
  clearSmsDependentChannels,
  
  // Preview
  smsTriggerPreviewRequested,
  smsTriggerPreviewSucceeded,
  smsTriggerPreviewFailed,
  
  // Trigger
  smsTriggerSendRequested,
  smsTriggerSendSucceeded,
  smsTriggerSendFailed,
  
  // Status
  smsTriggerStatusRequested,
  smsTriggerStatusSucceeded,
  smsTriggerStatusFailed,
  
  // UI State
  setSmsCurrentFilters,
  clearSmsPreview,
  clearSmsStatus,
  resetSmsTrigger,
  
  // Individual Policy Selection
  fetchPoliciesForSelectionRequested,
  fetchPoliciesForSelectionSucceeded,
  fetchPoliciesForSelectionFailed,
  togglePolicySelection,
  selectAllPolicies,
  clearPolicySelection,
  clearPolicies,
  triggerIndividualSmsRequested,
  setPolicyFilters,
  setIndividualSelectionMode
} = smsTriggerSlice.actions;

export default smsTriggerSlice.reducer;

